import axios from 'axios'
import wcs from '@/scripts/libs/wcs'
import makeDebug from 'debug'
import qs from 'qs'
import { Cookies } from '@/scripts/libs/cookies'
import roxConfig from '@/scripts/rox-panel-configuration/rox-config.js'

import { EYEMED_LOGIN } from '@/scripts/constants/eyemed'
const debug = makeDebug('pdp:eyeMed')
import {
  TRACKER_INSURANCE_TENTATIVE,
  TRACKER_ERROR_SOURCE_SERVER,
  TRACKER_EYEMED,
  TRACKER_INSURANCE,
  TRACKER_INSURANCE_BENEFITS,
  TRACKER_INSURANCE_APPLIED,
} from '@/scripts/constants/tracker'
import TealiumTrackingModule from '@/scripts/libs/tracking/tealium'

interface WcsBenefits {
  mandatoryServicePrice: string
  currencySymbol: string
  lensOriginalPrice: string
  lensFinalPrice: string
  frameOriginalPrice: string
  frameFinalPrice: string
  totalOriginalPrice: string
  totalFinalPrice: string
}

export default class WcsEyemed {
  static readonly insuranceIsEnabled = () => {
    const { roxLensPanel } = wcs.getStoreConfig()
    return roxLensPanel.isEyemedEnabled
  }

  static readonly isUserLoggedIn = (): boolean => {
    const isEyemedLoggedIn = Cookies.get(EYEMED_LOGIN)
    return !!isEyemedLoggedIn && isEyemedLoggedIn === 'true'
  }

  static submitEyeMedForm = async function (postParams: any, fromRoxPanel: boolean) {
    const endPoint = wcs.getEndPoint('roxInsuranceCustomer')
    const { catalogId, storeId, langId } = wcs.getStoreConfig()
    postParams.storeId = storeId
    postParams.catalogId = catalogId
    postParams.langId = langId
    try {
      debug('Form was submitted with these postParams %o', postParams)
      const result = await axios.post(endPoint, qs.stringify(postParams))

      const payload = result.data
      debug(payload)

      if (payload.ResultCode === 0) {
        // LOGIN SUBMITTED CORRECTLY
        if (payload.ErrorMessage) {
          // GENERIC ERROR WHEN LOGIN OK
          if (fromRoxPanel) {
            WcsEyemed.insuranceNotFoundEventHandler()
          }
          return {
            error: true,
            errorMessage: payload.ErrorMessage,
          }
        } else {
          TealiumTrackingModule.staticTrackEvent({
            event: TRACKER_INSURANCE_APPLIED,
            value: {
              Order_InsuranceCode: TRACKER_EYEMED,
              Order_InsuranceBenefits: TRACKER_INSURANCE_BENEFITS,
            },
          })
          // LOGIN OK
          if (fromRoxPanel) {
            WcsEyemed.insuranceLoginEventHandler()
          }
          Cookies.set(EYEMED_LOGIN, true)
          return {
            success: true,
            applied: true,
            error: false,
            pricingResponse: payload.pricingResponse,
          }
        }
      } else {
        TealiumTrackingModule.staticTrackEvent({
          event: TRACKER_INSURANCE_TENTATIVE,
          value: {
            Order_InsuranceCode: TRACKER_EYEMED,
            Error_Source: TRACKER_ERROR_SOURCE_SERVER,
            Error_Code: TRACKER_INSURANCE,
            Error_Message: '',
          },
        })
        // LOGIN CRITERIA ARE WRONG OR SERVER ERROR
        if (fromRoxPanel) {
          WcsEyemed.insuranceNotFoundEventHandler()
        }
        return {
          error: true,
          errorMessage: payload.ErrorMessage,
        }
      }
    } catch (e: any) {
      debug(e.message)
      if (fromRoxPanel) {
        WcsEyemed.insuranceNotFoundEventHandler()
      }
      return {
        error: true,
      }
    }
  }

  private static insuranceNotFoundEventHandler = () => {
    const insuranceNotFoundEvent = roxConfig.insuranceModule.getEvents().INSURANCE_NOT_FOUND
    window.dispatchEvent(new CustomEvent(insuranceNotFoundEvent))
  }

  private static insuranceLoginEventHandler = () => {
    const insuranceLoginEvent = roxConfig.insuranceModule.getEvents().INSURANCE_LOGIN
    window.dispatchEvent(new CustomEvent(insuranceLoginEvent))
    const insuranceEnabledEvent = roxConfig.insuranceModule.getEvents().INSURANCE_ENABLED
    window.dispatchEvent(new CustomEvent(insuranceEnabledEvent))
  }

  static makeBenefitPricingCall = async function (postParams: any) {
    const endPoint = wcs.getEndPoint('roxInsurancePricing')
    try {
      debug('Form was submitted with these postParams %o', postParams)
      const result = await axios.post(endPoint, qs.stringify(postParams))

      const payload = result.data
      debug('Benefit Pricing Call Payload %o', payload)

      if (payload.pricingResultCode == 0) {
        return {
          success: true,
          ...WcsEyemed.sanitizeBenefitsPayload(payload),
        }
      }
    } catch (e: any) {
      debug(e.message)
    }
  }

  private static sanitizeBenefitsPayload = (benefits: WcsBenefits) => {
    const mandatoryServices = Object.entries(benefits).filter(([key]) =>
      key.toLowerCase().includes('mandatoryservice')
    )

    const mandatoryServicePrice = +benefits.mandatoryServicePrice.replace(
      benefits.currencySymbol,
      ''
    )

    const lensPrices = {
      original: +benefits.lensOriginalPrice.replace(benefits.currencySymbol, ''),
      final: +benefits.lensFinalPrice.replace(benefits.currencySymbol, ''),
    }

    const totalPrices = {
      original: +benefits.totalOriginalPrice.replace(benefits.currencySymbol, ''),
      final: +benefits.totalFinalPrice.replace(benefits.currencySymbol, ''),
    }

    if (mandatoryServices.length >= 2) {
      const mandatoryServicesPrices = Object.values(Object.fromEntries(mandatoryServices)).map(
        (el) => +el.replace(benefits.currencySymbol, '')
      )
      const fixedLensOriginalPrice = lensPrices.original + mandatoryServicePrice
      const fixedLensFinalPrice = lensPrices.final + Math.min(...mandatoryServicesPrices)

      benefits.lensOriginalPrice = benefits.currencySymbol + fixedLensOriginalPrice.toFixed(2)
      benefits.lensFinalPrice = benefits.currencySymbol + fixedLensFinalPrice.toFixed(2)
    } else if (benefits.mandatoryServicePrice) {
      const fixedLensOriginalPrice = lensPrices.original + mandatoryServicePrice
      const fixedLensFinalPrice = lensPrices.final + mandatoryServicePrice

      benefits.lensOriginalPrice = benefits.currencySymbol + fixedLensOriginalPrice.toFixed(2)
      benefits.lensFinalPrice = benefits.currencySymbol + fixedLensFinalPrice.toFixed(2)
    }

    benefits.totalOriginalPrice = benefits.currencySymbol + totalPrices.original.toFixed(2)
    benefits.totalFinalPrice = benefits.currencySymbol + totalPrices.final.toFixed(2)

    return benefits
  }
}
